import { PICK_VERIFY_SHIP } from '@/lib/pickVerifyShip';

/**
 * The Verify screen's routing and copy decisions (design §7.3, §11.8), pure so they are pinned by
 * verifyScreen.test.ts. The page and its data file only act on what these return.
 *
 * Server messages are matched by their LEADING words — the server's PICK_VERIFY_* constants
 * (libs/services pick-verify/workflow.ts) and the Verify refusals (verify.service.ts). The warehouse
 * cannot import them (Docker build context), so keep the prefixes below in step with those files.
 */

/** What the SalesOrderVerifyList selection needs for these decisions. */
export type VerifyListState = {
  vendorStatus?: string | null;
  workflow?: { workflow?: string | null; stage?: string | null } | null;
  lines?: readonly VerifyLineState[] | null;
  totalRequired?: number | null;
  totalScanned?: number | null;
  complete?: boolean | null;
  overScanned?: boolean | null;
};

export type VerifyLineState = {
  key: string;
  kind?: string | null;
  productName?: string | null;
  required: number;
  scanned: number;
  orphan?: boolean | null;
  /** A controlled product this operator may not verify. */
  controlledLocked?: boolean | null;
};

/**
 * Which screen a Verify list belongs on:
 *  - `scan`      — at `picking`, Pick complete (stage verify; a verified order written back to
 *                  picking also reads verify — the server already maps it);
 *  - `documents` — verified and waiting to ship: the box documents (reprint) + Reopen Verify;
 *  - `pick`      — still in its Pick step → the Pick screen;
 *  - `lost`      — anything else (cancelled, on hold, split, shipped, not Option 2) → the list.
 */
export type VerifyScreen = 'scan' | 'documents' | 'pick' | 'lost';

export const verifyScreenFor = (
  list: VerifyListState | null | undefined
): VerifyScreen => {
  if (!list || list.workflow?.workflow !== PICK_VERIFY_SHIP) return 'lost';
  const stage = list.workflow?.stage;
  if (list.vendorStatus === 'picking') {
    if (stage === 'verify' || stage === 'verified') return 'scan';
    return 'pick';
  }
  if (list.vendorStatus === 'shipping' && stage === 'verified') return 'documents';
  return 'lost';
};

/** Mirror of PICK_VERIFY_NOT_PICKING_MESSAGE — the copy for an order that left picking. */
export const VERIFY_ORDER_LEFT_PICKING_MESSAGE =
  'This order is no longer in picking (cancelled, on hold or split). Return any picked goods to stock.';
export const VERIFY_ORDER_SHIPPED_MESSAGE =
  'This order has already shipped — there is nothing left to verify.';
export const VERIFY_ORDER_NOT_FOUND_MESSAGE =
  'This order could not be found. It may have been split or removed — check the Sales Orders list.';

/** Why the screen is sending the operator back to the list, for an order the list says is gone. */
export const verifyLostOrderMessage = (
  list: VerifyListState | null | undefined
): string => {
  if (
    list?.vendorStatus === 'shipped' ||
    list?.vendorStatus === 'customer_pickup' ||
    list?.vendorStatus === 'completed'
  ) {
    return VERIFY_ORDER_SHIPPED_MESSAGE;
  }
  return VERIFY_ORDER_LEFT_PICKING_MESSAGE;
};

/**
 * A refusal that says the ORDER (not the unit) is no longer where this screen thinks it is:
 *  - `lost`     — PICK_VERIFY_NOT_PICKING_MESSAGE (cancelled / held / split under us);
 *  - `standard` — PICK_VERIFY_NOT_THIS_ORDER_MESSAGE (not an Option-2 order);
 *  - `pick`     — PICK_VERIFY_PICK_FIRST_MESSAGE (back in, or never out of, the Pick step);
 *  - `verified` — PICK_VERIFY_VERIFIED_LOCKED_MESSAGE (someone else completed Verify);
 *  - `notFound` — the order itself is not found (NOT_FOUND "Sales order not found").
 * Null for every other refusal (over-scan, not on order, duplicate…): those are about the unit.
 */
export type VerifyOrderRefusal = 'lost' | 'standard' | 'pick' | 'verified' | 'notFound';

const ORDER_REFUSAL_PREFIXES: [string, VerifyOrderRefusal][] = [
  ['This order is no longer in picking', 'lost'],
  ['This order follows the standard fulfillment workflow', 'standard'],
  ['Finish the Pick step first', 'pick'],
  ['This order has been verified', 'verified']
];

type GraphQLErrorLike = {
  message?: string | null;
  extensions?: { code?: string | null; reason?: string | null } | null;
};

const graphQLErrorsOf = (error: unknown): GraphQLErrorLike[] => {
  const list = (error as { graphQLErrors?: unknown } | null | undefined)?.graphQLErrors;
  return Array.isArray(list) ? (list as GraphQLErrorLike[]) : [];
};

export const classifyVerifyOrderRefusal = (error: unknown): VerifyOrderRefusal | null => {
  for (const graphQLError of graphQLErrorsOf(error)) {
    const message =
      typeof graphQLError?.message === 'string' ? graphQLError.message.trim() : '';
    if (!message) continue;
    const match = ORDER_REFUSAL_PREFIXES.find(([prefix]) => message.startsWith(prefix));
    if (match) return match[1];
    if (
      graphQLError?.extensions?.code === 'NOT_FOUND' &&
      /^(sales order|order) not found/i.test(message)
    ) {
      return 'notFound';
    }
  }
  return null;
};

/**
 * The `extensions.reason` of a Verify Add refused because its unit is in quarantine
 * (VERIFY_UNIT_QUARANTINED_REASON, libs/services pick-verify/verify.service.ts) — the cue for the
 * stock prompt instead of a bare refusal toast.
 */
export const VERIFY_UNIT_QUARANTINED_REASON = 'VERIFY_UNIT_QUARANTINED';
/** How each of those refusals begins (the server's QUARANTINED_LEAD), for the toast title. */
const QUARANTINED_PREFIX = 'In quarantine';

/** Whether a scan's refusal is "the unit is in quarantine" — by its reason, never by its wording. */
export const isVerifyQuarantineRefusal = (error: unknown): boolean =>
  graphQLErrorsOf(error).some(
    (graphQLError) => graphQLError?.extensions?.reason === VERIFY_UNIT_QUARANTINED_REASON
  );

/** The toast title for a unit refusal, from the server's wording, so a glance says what happened. */
export const verifyRefusalTitle = (message: string, mode: 'add' | 'remove'): string => {
  const text = (message || '').trim();
  if (text.startsWith(QUARANTINED_PREFIX)) return 'In quarantine';
  if (text.startsWith('Over-scan')) return 'Over-scan';
  if (text.startsWith('Duplicate')) return 'Duplicate serial';
  if (/ is not on this sales order\.?$/.test(text)) return 'Not on this order';
  if (text.startsWith('This product is serialized')) return 'Serial required';
  if (text.startsWith('Pallet (SSCC) labels')) return 'Pallet label';
  if (/ is a controlled substance \(/.test(text)) return 'Controlled product';
  return mode === 'remove' ? 'Could not remove' : 'Not verified';
};

/**
 * Mirror of the server's over-scan refusals (VERIFY_OVER_SCAN_ALL_MESSAGE /
 * VERIFY_OVER_SCAN_REMAINING_MESSAGE, verify.service.ts), for the Add the device refuses itself:
 * `remaining` is what the group still needs, counting the units still saving.
 */
export const verifyOverScanMessage = (
  required: number,
  remaining: number,
  name: string
): string =>
  remaining > 0
    ? `Over-scan: only ${remaining} more unit(s) of ${name} are needed.`
    : `Over-scan: all ${required} unit(s) of ${name} are already verified.`;

export const VERIFY_SCAN_FALLBACK_MESSAGE =
  'Could not verify this unit. Please try again.';
export const VERIFY_REMOVE_FALLBACK_MESSAGE =
  'Could not remove this unit. Please try again.';

/**
 * Verify's camera profile: PRODUCT (ITEM symbologies + UPC-A / EAN-13, UPC-E, EAN-8) when any group
 * is counted by product code, ITEM otherwise — an all-serialized order never needs the linear
 * codes, and not decoding them is what keeps the camera on the DataMatrix of an Rx pack.
 */
export const verifyNeedsRetailCodes = (
  lines: readonly VerifyLineState[] | null | undefined
) => (lines || []).some((line) => line.kind === 'BULK' || line.kind === 'UPC');

/** "Back to Pick" exists only while nothing is verified (the server refuses otherwise). */
export const verifyCanGoBackToPick = (
  list: VerifyListState | null | undefined
): boolean => !!list && (list.totalScanned || 0) === 0;

/**
 * Why "Short — split order" is unavailable, or null when it may be tried. Mirrors the server's
 * pre-split checks (splitOrderAtPicking, Option-2 branch) so the operator is told before, not after.
 */
export const verifySplitBlockedReason = (
  list: VerifyListState | null | undefined
): string | null => {
  if (!list) return 'Loading…';
  if ((list.totalScanned || 0) === 0) {
    return 'Nothing is verified yet, so there is nothing to split off. If the tote does not hold what Pick found, use Back to Pick.';
  }
  if (list.complete) return 'Everything is verified — complete Verify instead.';
  const extra = (list.lines || []).some(
    (line) => line.orphan || line.scanned > line.required
  );
  if (list.overScanned || extra) {
    return 'Remove the extra unit(s) first — a split ships only what the order asks for.';
  }
  return null;
};

/** Units still to verify across the groups the order asks for. */
export const verifyUnitsRemaining = (
  lines: readonly VerifyLineState[] | null | undefined
) =>
  (lines || []).reduce(
    (sum, line) => (line.orphan ? sum : sum + Math.max(0, line.required - line.scanned)),
    0
  );

/**
 * One line under the Complete button when something scanned does not belong (null otherwise).
 * Units still to scan are the count beside "Units verified", not a second line here.
 */
export const verifyCompleteBlockedReason = (
  list: VerifyListState | null | undefined
): string | null => {
  if (!list || list.complete) return null;
  const lines = list.lines || [];
  const orphan = lines.find((line) => line.orphan);
  if (orphan)
    return `${
      orphan.productName || 'A product'
    } is not on this order — remove the unit(s).`;
  const over = lines.find((line) => line.scanned > line.required);
  if (over) {
    return `${over.productName || 'A product'} is over-scanned by ${
      over.scanned - over.required
    } — remove the extra unit(s).`;
  }
  const locked = verifyUnitsRemaining(lines.filter((line) => line.controlledLocked));
  if (locked > 0) {
    return `${locked} unit(s) of controlled products need a user with the "Fulfill Controlled Sales Order" permission to verify them.`;
  }
  return null;
};

/**
 * The note above the list when groups are locked for this operator (null when none are): they are
 * listed and counted like the rest, and somebody holding the permission scans them.
 */
export const verifyLockedNote = (
  lines: readonly VerifyLineState[] | null | undefined
): string | null => {
  const count = (lines || []).filter((line) => line.controlledLocked).length;
  if (count < 1) return null;
  return `${
    count === 1 ? '1 controlled product is' : `${count} controlled products are`
  } locked — you need the "Fulfill Controlled Sales Order" permission to verify ${
    count === 1 ? 'it' : 'them'
  }. Verify the other products; a user with the permission scans the rest.`;
};

/**
 * The line under the verified count, read off the counts the operator sees (`lines` = the server's
 * list plus the scans still saving) — never the server's `complete` against those counts, which
 * lags them by a round trip. While scans are saving, extra units are named only when a row shown
 * really holds more than it needs; with nothing saving the rows ARE the server's, and so is the
 * last word (`complete` also weighs what no row shows, e.g. `overScanned`).
 */
export const verifySummaryMessage = (
  lines: readonly VerifyLineState[] | null | undefined,
  { pending, complete }: { pending: number; complete?: boolean | null }
): string => {
  const remaining = verifyUnitsRemaining(lines);
  if (remaining > 0) return `${remaining} to go`;
  const extra = (lines || []).some((line) => line.scanned > line.required);
  if (extra || (pending === 0 && !complete)) return 'Remove the extra units.';
  return pending > 0 ? 'All counted' : 'Everything is verified.';
};

/** The two documents printed at Verify, by what the docs query returns. */
export type PackDocumentLike = { link?: string | null } | null | undefined;

export const packDocumentsReady = (
  order:
    | {
        packInvoiceDocument?: PackDocumentLike;
        packPackingListDocument?: PackDocumentLike;
      }
    | null
    | undefined
) => ({
  invoice: !!order?.packInvoiceDocument?.link,
  packingList: !!order?.packPackingListDocument?.link,
  all: !!order?.packInvoiceDocument?.link && !!order?.packPackingListDocument?.link
});
